import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { ProgramAccessRow } from '../services/program-rules.service';

export interface ProgramPugChooserParams {
  row: ProgramAccessRow;
  userFullName: string;
  isReadOnly: boolean;
  /** PUG ids this admin supervises; null means unrestricted (PAA). */
  supervisedPugIds: Set<string> | null;
}

/**
 * Result of the chooser. `winnerPugId` is the group that should govern the program;
 * `null` means "None" — the user is excluded from every candidate group on this program,
 * which is a legitimate, deliberate configuration and not a failure state.
 * The dialog resolves to `undefined` when cancelled.
 */
export interface ProgramPugChooserResult {
  winnerPugId: string | null;
}

/**
 * "Which group is the correct and active one?"
 *
 * A user in two Program User Groups that both reach the same Program is BLOCKED from ordering
 * it — Proximity requires exactly one included group per (user x program). Resolving that is
 * a single choice, so this is a radio list: picking a winner includes it and excludes every
 * other candidate, which is precisely what ProgramUserGroupExclusion rows encode.
 *
 * See Docs/program-user-group-access.md §6.3.
 */
@Component({
  selector: 'prox-program-pug-chooser-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, RadioButtonModule],
  template: `
    <div class="flex h-full w-full flex-col">
      <p class="mb-1 text-base">
        {{ userFullName }} belongs to
        {{ row.choices.length }}
        {{ row.choices.length === 1 ? 'group that reaches' : 'groups that reach' }}
        this program.
      </p>
      <p class="mb-4 text-lg font-semibold">Which group is the correct and active one?</p>

      <div class="flex flex-col gap-3 overflow-auto">
        @for (choice of row.choices; track choice.programUserGroupId) {
          <label
            class="flex cursor-pointer items-start gap-3 rounded-md border p-3"
            [class.border-gray-200]="selectedPugId !== choice.programUserGroupId"
            [class.border-primary]="selectedPugId === choice.programUserGroupId"
            [class.opacity-60]="!isSelectable(choice.programUserGroupId)">
            <p-radioButton
              name="winner"
              [value]="choice.programUserGroupId"
              [(ngModel)]="selectedPugId"
              [disabled]="!isSelectable(choice.programUserGroupId)"></p-radioButton>
            <span class="flex flex-col">
              <span class="font-medium">
                {{ choice.programUserGroupName }}
                @if (!choice.isPugActive) {
                  <span class="ml-2 text-sm font-normal text-amber-600">({{ choice.pugStatusName }})</span>
                }
              </span>
              @if (choice.detail) {
                <span class="text-sm text-gray-600">{{ choice.detail }}</span>
              }
              @if (!isSelectable(choice.programUserGroupId)) {
                <span class="text-xs text-gray-500">Not in your supervised scope.</span>
              }
            </span>
          </label>
        }

        <label
          class="flex cursor-pointer items-start gap-3 rounded-md border p-3"
          [class.border-gray-200]="selectedPugId !== NONE"
          [class.border-primary]="selectedPugId === NONE">
          <p-radioButton name="winner" [value]="NONE" [(ngModel)]="selectedPugId"></p-radioButton>
          <span class="flex flex-col">
            <span class="font-medium">None — this user should not order this program</span>
            <span class="text-sm text-gray-600">
              Keeps every membership; excludes the user from this program only.
            </span>
          </span>
        </label>
      </div>

      <p class="mt-4 text-sm text-gray-600">Currently: {{ currentStateSentence() }}</p>

      <div class="flex pt-4 items-center">
        <button
          pButton
          id="pugChooserApply"
          [disabled]="isReadOnly || !hasChanged()"
          (click)="onApply()"
          label="Apply"
          icon="pi pi-check"
          class="mr-2"></button>
        <button pButton (click)="onCancel()" label="Cancel" icon="pi pi-times" class="mr-2"></button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
    `,
  ],
})
export class ProgramPugChooserDialogComponent implements OnInit {
  /** Sentinel for the "None" option — a real choice, not an absence of one. */
  readonly NONE = '__none__';

  row!: ProgramAccessRow;
  userFullName = '';
  isReadOnly = false;
  supervisedPugIds: Set<string> | null = null;

  selectedPugId: string = this.NONE;
  private initialPugId: string = this.NONE;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig<ProgramPugChooserParams>,
  ) {}

  public static async openDialog(dialogService: DialogService, data: ProgramPugChooserParams) {
    const ref = dialogService.open(ProgramPugChooserDialogComponent, {
      closable: false,
      draggable: true,
      modal: true,
      width: '640px',
      header: data.row.programName,
      data,
    });
    return (await firstValueFrom(ref.onClose)) as ProgramPugChooserResult | undefined;
  }

  ngOnInit(): void {
    const params = this.config.data!;
    this.row = params.row;
    this.userFullName = params.userFullName;
    this.isReadOnly = params.isReadOnly;
    this.supervisedPugIds = params.supervisedPugIds;

    // Pre-select only when the current configuration is already valid. A blocked program has
    // two allowed groups and therefore no defensible default — leaving it unselected forces
    // the deliberate choice that resolves it.
    const allowed = this.row.choices.filter((c) => c.isAllowed);
    this.initialPugId = allowed.length === 1 ? allowed[0].programUserGroupId : allowed.length === 0 ? this.NONE : '';
    this.selectedPugId = this.initialPugId;
  }

  /** Scoped admins can only hand a program to a group they supervise. */
  isSelectable(pugId: string): boolean {
    if (this.isReadOnly) return false;
    if (!this.supervisedPugIds) return true;
    return this.supervisedPugIds.has(pugId);
  }

  hasChanged(): boolean {
    return this.selectedPugId !== '' && this.selectedPugId !== this.initialPugId;
  }

  currentStateSentence(): string {
    const allowed = this.row.choices.filter((c) => c.isAllowed);
    if (allowed.length > 1) {
      return `${allowed.length} groups are active on this program, so it is blocked — the user cannot order it at all.`;
    }
    if (allowed.length === 1) {
      return `orders route through ${allowed[0].programUserGroupName}.`;
    }
    return 'the user is excluded from every group on this program.';
  }

  onApply(): void {
    const result: ProgramPugChooserResult = {
      winnerPugId: this.selectedPugId === this.NONE ? null : this.selectedPugId,
    };
    this.ref.close(result);
  }

  onCancel(): void {
    this.ref.close();
  }
}
